import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const faqs = [
  {
    question: '¿Cómo sé qué talla de anillo necesito?',
    answer:
      'Incluimos un kit de medición gratuito con tu pedido. También puedes pedir solo el kit de tallas antes de comprometer tu reserva. Cubrimos las tallas del 5 al 13 (US), que equivalen a los tamaños europeos del 49 al 68.',
  },
  {
    question: '¿HELIX es compatible con mi smartphone?',
    answer:
      'Sí. La app HELIX es compatible con iOS 15 o superior y Android 10 o superior. Cualquier teléfono fabricado después de 2019 es totalmente compatible. La sincronización se hace por Bluetooth 5.0 de bajo consumo.',
  },
  {
    question: '¿Qué tan preciso es el monitor de ritmo cardíaco?',
    answer:
      'Nuestros sensores ópticos PPG de cuarta generación ofrecen una precisión del 99.2% en comparación con monitores clínicos certificados. Los algoritmos han sido validados en un estudio con más de 3,000 participantes.',
  },
  {
    question: '¿Puedo ducharse y nadar con HELIX?',
    answer:
      'Completamente. HELIX tiene certificación IP68, lo que significa resistencia al agua hasta 100 metros de profundidad. Es apto para ducha, piscina, surf y snorkel. El titanio no se oxida ni se decolora.',
  },
  {
    question: '¿Cuánto tiempo dura la batería?',
    answer:
      'Con el monitoreo continuo activado (la configuración estándar), la batería dura entre 6 y 8 días. En modo ultra-ahorro puede alcanzar los 12 días. La carga completa desde cero toma 80 minutos con el cargador magnético incluido.',
  },
  {
    question: '¿Cuándo se entregan los pedidos de preventa?',
    answer:
      'Los fundadores recibirán sus unidades en el primer lote de envíos, estimado para el primer trimestre de 2025. Recibirás actualizaciones mensuales del estado de producción y tu número de seguimiento en cuanto se procese tu envío.',
  },
  {
    question: '¿Qué pasa si no me gusta o no me funciona?',
    answer:
      'Tienes 30 días desde la recepción para devolver HELIX sin ningún coste y sin dar explicaciones. El reembolso es completo e inmediato. Además, todos los anillos incluyen garantía de fabricación de 1 año (2 años para fundadores).',
  },
  {
    question: '¿HELIX reemplaza a un médico o a un diagnóstico clínico?',
    answer:
      'HELIX es un dispositivo de bienestar personal, no un dispositivo médico certificado. Los datos son de alta precisión y de gran valor informativo, pero no reemplazan el criterio médico profesional. Si detectas irregularidades, consulta siempre a tu médico.',
  },
];

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-stone-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left cursor-pointer group"
      >
        <span className="font-body text-sm font-medium text-stone-800 pr-8 group-hover:text-amber-700 transition-colors leading-relaxed">
          {question}
        </span>
        <div
          className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full border transition-all duration-200 ${
            open
              ? 'bg-amber-600 border-amber-600 text-white'
              : 'border-stone-200 text-stone-400 group-hover:border-amber-300 group-hover:text-amber-600'
          }`}
        >
          <i className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
        </div>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'max-h-64 pb-6' : 'max-h-0'
        }`}
      >
        <p className="font-body text-sm text-stone-500 leading-relaxed pr-12">{answer}</p>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: listRef, isVisible: listVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="faq" className="py-28 bg-white w-full">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left */}
          <div
            ref={titleRef}
            className={`lg:col-span-4 scroll-hidden ${titleVisible ? 'scroll-visible' : ''}`}
          >
            <span className="font-body text-xs font-semibold tracking-widest text-amber-700 uppercase block mb-4">
              Preguntas frecuentes
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-stone-900 leading-tight mb-6">
              Resolvemos
              <br />
              <em className="not-italic font-semibold">tus dudas.</em>
            </h2>
            <p className="font-body text-stone-500 text-sm leading-relaxed mb-8">
              Si tienes alguna pregunta adicional, nuestro equipo está disponible
              en el chat o por email. Respondemos en menos de 2 horas en días laborables.
            </p>
            <a
              href="mailto:hola@helixring.com"
              rel="nofollow"
              className="inline-flex items-center gap-2 bg-stone-900 text-white font-body text-sm font-medium px-6 py-3 rounded-full hover:bg-amber-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line" />
              Contactar soporte
            </a>
          </div>

          {/* Right */}
          <div
            ref={listRef}
            className={`lg:col-span-8 scroll-hidden ${listVisible ? 'scroll-visible' : ''}`}
          >
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
